import { useEffect, useMemo } from 'react'

import { useKeyMetricActions } from 'redux/actions/KeyMetrics'
import { useMetricActions } from 'redux/actions/Metrics'
import { useMetricSegmentAttributeOptions } from 'redux/hooks/AttributeOptions'
import { useKeyMetricRawData } from 'redux/hooks/KeyMetrics'
import { useMetric, useMetricRawData } from 'redux/hooks/Metrics'

import { TKeyMetric, TKeyMetricDataBody } from 'types/GlobalKeyMetric'
import {
  ISelectMetric,
  MetricStatus,
  TMetricDataRow,
  TMetricQueryParams,
  TMetricSettings,
  TDefinitionSlice
} from 'types/GlobalMetric'

/**
 * The two ways to point at metric data: a key metric (fetched through
 * POST /v1/key-metrics/:id/data, which also computes the target/budget
 * series server-side) or a plain metric with explicit settings (fetched
 * through POST /v1/metrics/:id, never has a target).
 */
export type TMetricDataSource =
  | { keyMetric: TKeyMetric; id?: never; settings?: never }
  | { keyMetric?: undefined; id: string; settings: TMetricSettings }

// technical name of the calendar attribute option that segments per day
const DAY_TECHNICAL_NAME = 'year_month_day'

export type TMetricData = {
  data: TMetricDataRow[]
  key_figure?: { value: number | null }
  segment_attribute_option_id: string | null
  target: TMetricDataRow[] | null
}

/**
 * Owns the full data flow for one metric series regardless of source:
 * reads the right store, triggers the fetch when nothing is cached (both
 * thunks dedupe identical in-flight requests), and normalizes the result
 * so consumers never branch on metric vs key metric.
 */
export const useMetricData = (
  source: TMetricDataSource,
  requestedQueryParams?: TMetricQueryParams,
  includeKeyFigure?: boolean,
  options?: {
    // one point per day instead of the metric's default segment
    daily?: boolean
  }
): {
  metric: ISelectMetric | null
  missingSlices: TDefinitionSlice[]
  rawData: TMetricData | null
  /** The settings the data was fetched with (a key metric's stored ones). */
  settings: TMetricSettings
  error: boolean
} => {
  const { keyMetric } = source

  const metricId = keyMetric ? keyMetric.metric.id : source.id
  const settings = keyMetric ? keyMetric.metric.settings : source.settings

  const { tryGetKeyMetricData } = useKeyMetricActions()
  const { tryFetchMetricData } = useMetricActions()

  const metric = useMetric(metricId, settings.variant_kpi_template_id)

  const dayOption = useMetricSegmentAttributeOptions(
    options?.daily ? metric?.fact_table_id : null
  ).find((option) => option.technical_name === DAY_TECHNICAL_NAME)

  const queryParams = useMemo(
    () =>
      dayOption
        ? { ...requestedQueryParams, segment_by: dayOption.id }
        : requestedQueryParams,
    [requestedQueryParams, dayOption?.id]
  )

  const keyMetricBody: TKeyMetricDataBody | undefined = useMemo(
    () =>
      queryParams
        ? {
            query_params: {
              filter: queryParams.filter,
              from_date: queryParams.from_date,
              period_filter: queryParams.period_filter,
              segment_by: queryParams.segment_by ?? undefined,
              to_date: queryParams.to_date
            }
          }
        : undefined,
    [
      queryParams?.filter,
      queryParams?.from_date,
      queryParams?.period_filter,
      queryParams?.segment_by,
      queryParams?.to_date
    ]
  )

  // Both stores are read unconditionally (rules of hooks); the inactive
  // path is disabled via an empty/undefined id and returns nothing.
  const [keyMetricRawData, keyMetricErrorStatus] = useKeyMetricRawData(
    keyMetric?.id ?? '',
    keyMetricBody
  )
  const [metricRawData, metricRawDataError, rejectedSlices] = useMetricRawData({
    id: keyMetric ? undefined : metricId,
    settings,
    queryParams,
    includeKeyFigure
  })

  const rawData: TMetricData | null = useMemo(() => {
    if (keyMetric) {
      return keyMetricRawData
    }

    return metricRawData ? { ...metricRawData, target: null } : null
  }, [keyMetric, keyMetricRawData, metricRawData])

  const error = keyMetric ? keyMetricErrorStatus !== null : metricRawDataError

  // Skärningar the data request was rejected over count as missing too.
  const missingSlices = useMemo(() => {
    const unconfigured = (metric?.definition_slices ?? []).filter(
      (slice) => !slice.configured
    )

    return [
      ...unconfigured,
      ...rejectedSlices.filter(
        (rejected) => !unconfigured.some((slice) => slice.id === rejected.id)
      )
    ]
  }, [metric?.definition_slices, rejectedSlices])

  useEffect(() => {
    if (rawData || error) return

    if (!metric) return

    // The request cannot succeed without the slices, so do not make it.
    if (missingSlices.length > 0) return

    // Nor without a formula.
    if (metric.status === MetricStatus.MISSING_DEFINITION) return

    if (keyMetric) {
      tryGetKeyMetricData(keyMetric.id, keyMetricBody)
    } else {
      tryFetchMetricData(
        metricId,
        settings,
        queryParams,
        undefined,
        includeKeyFigure
      )
    }
  }, [
    keyMetric,
    metricId,
    settings,
    queryParams,
    keyMetricBody,
    includeKeyFigure,
    rawData,
    error,
    metric,
    missingSlices,
    tryFetchMetricData,
    tryGetKeyMetricData
  ])

  return { metric, missingSlices, rawData, settings, error }
}
